'use client'

import { useTranslations } from 'next-intl'
import { useRouter } from 'next/navigation'
import React, { useEffect } from 'react'

import { usePathname } from '~/i18n/routing'
import { trackEvent } from '~/lib/analytics/client'
import { routes } from '~/lib/constants'
import { cn } from '~/lib/utils'
import {
  Stepper,
  StepperIndicator,
  StepperItem,
  StepperSeparator,
  StepperTitle,
  StepperTrigger,
} from '~/modules/common/components/ui/stepper'

export function CartStepper() {
  const pathname = usePathname()
  const navigate = useRouter()
  const t = useTranslations('common.cartStepper')

  const steps = [
    {
      path: routes.cart,
      label: t('cart.label'),
      shortLabel: t('cart.shortLabel'),
      step: 0,
      stepName: 'carrito',
    },
    {
      path: routes.address,
      label: t('shipping.label'),
      shortLabel: t('shipping.shortLabel'),
      step: 1,
      stepName: 'envio',
    },
    {
      path: routes.payment,
      label: t('payment.label'),
      shortLabel: t('payment.shortLabel'),
      step: 2,
      stepName: 'pago',
    },
  ] as const

  const currentStepIndex
    = steps.find((step) => {
      return step.path === pathname
    })?.step ?? 0

  useEffect(() => {
    if (currentStepIndex === 0) {
      trackEvent('car_pasos_1', {
        path: pathname,
        step_name: 'carrito',
        step_number: 1,
      })
    }
    else if (currentStepIndex === 1) {
      trackEvent('car_pasos_2', {
        path: pathname,
        step_name: 'envio',
        step_number: 2,
      })
    }
    else {
      trackEvent('car_pasos_3', {
        path: pathname,
        step_name: 'pago',
        step_number: 3,
      })
    }
  }, [currentStepIndex, pathname])

  const handleStepClick = (step: (typeof steps)[number]) => {
    navigate.push(step.path)
  }

  return (
    <div className="flex justify-center mb-16 max-sm:mb-8">
      <Stepper
        value={currentStepIndex}
        className="w-full max-w-[590px] px-4 max-sm:px-2"
      >
        {steps.map((step, index) => (
          <StepperItem
            key={step.path}
            step={index}
            completed={index < currentStepIndex}
            className="relative flex-1 !flex-col"
          >
            <StepperTrigger
              className={cn(
                'flex-col gap-3 w-full max-sm:gap-2',
                index >= currentStepIndex && 'pointer-events-none',
              )}
              onClick={() => handleStepClick(step)}
              disabled={index > currentStepIndex}
            >
              <StepperIndicator />
              <div className="flex flex-col items-center">
                {/* Show short label on mobile, full label on desktop */}
                <StepperTitle className="max-sm:hidden">{step.label}</StepperTitle>
                <StepperTitle className="hidden max-sm:block text-xs">
                  {step.shortLabel}
                </StepperTitle>
              </div>
            </StepperTrigger>

            {index < steps.length - 1 && (
              <StepperSeparator className="absolute inset-x-0 left-[calc(50%+17px)] top-[19.5px] -order-1 -translate-y-1/2 w-[calc(100%-34px)] max-sm:left-[calc(50%+15px)] max-sm:w-[calc(100%-30px)] max-sm:top-[17px] max-sm:scale-75" />
            )}
          </StepperItem>
        ))}
      </Stepper>
    </div>
  )
}
